import moment from "moment"
import type { StrictCheckboxProps } from "semantic-ui-react"
import { Checkbox, Loader, Table } from "semantic-ui-react"
import type { FoodOrder } from "types"
import { sortDirection } from "utils"

/**
 * What the invoice table accepts.
 *
 * Established from its only call site, `./index.tsx:212`. The sort trio
 * (`column`/`direction`/`dispatch`) is the parent's `useReducer` state, which
 * seeds `sortcolumn` and `direction` as `null`, hence the `null` arms.
 *
 * `direction` is Semantic's own `'ascending' | 'descending'` plus that `null`;
 * the header ternaries here emit `null` for every non-sorted column, which
 * Semantic's `sorted` prop does not declare — a disagreement that predates this
 * type.
 *
 * SUSPECTED DEFECT (table.tsx:77) — REPORTED, NOT FIXED. The loader below is
 * gated on `props.order`, singular, which no caller passes and this type
 * therefore does not declare. The read is permanently `undefined`, so `!undefined`
 * is always true and the `<Loader/>` renders on *every* pass, including once the
 * rows are present. The intended gate is almost certainly `props.orders`. Left as
 * written: removing the stray render is a visible behaviour change.
 */
type InvoiceTableProps = {
    /**
     * The currently sorted column key. Always `undefined` in practice — see the
     * SUSPECTED DEFECT note above about the parent destructuring `column` from a
     * reducer that only ever writes `sortcolumn`.
     */
    column?: string | null | undefined;
    direction?: 'ascending' | 'descending' | null | undefined;
    /**
     * Read but never passed: the loader gate below is spelled `props.order`,
     * singular. Declared so the read type-checks and the gap stays visible.
     */
    order?: unknown;
    /** The parent's reducer dispatch; every call here is a `CHANGE_SORT`. */
    dispatch: (action: { type: 'CHANGE_SORT' | 'FILTER' | 'CLEAR_FILTERS'; sortcolumn?: string | undefined }) => void;
    /** Ids currently ticked, driving each row checkbox's `checked`. */
    selectedOrderIds: string[];
    /** Rows to render, already sorted and filtered by the parent's reducer. */
    orders?: FoodOrder[];
    /** Fires as a row checkbox toggles; only `.checked` is read of `checkprops`. */
    addExportData: (event: React.FormEvent<HTMLInputElement>, checkprops: StrictCheckboxProps, orderId?: string) => void;
    /** Header checkbox handler. */
    selectAll: (event: React.FormEvent<HTMLInputElement>, checkprops: StrictCheckboxProps) => void;
}

function InvoiceTable(props: InvoiceTableProps){
    let foodPriceTotal:number = 0
    let shippingFeesTotal:number = 0
    return (
        <div className="table-div">
            <Table sortable celled singleLine structured>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell content={<Checkbox onChange = {props.selectAll}/>}/>
                        <Table.HeaderCell 
                        {...sortDirection(props.column === 'orderId' ? props.direction : null)}
                        onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'orderId' })}>
                        Order ID</Table.HeaderCell>
                        <Table.HeaderCell {...sortDirection(props.column === 'orderName' ? props.direction : null)}
                            onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'orderName' })}>
                            Order Name</Table.HeaderCell>
                        <Table.HeaderCell {...sortDirection(props.column === 'orderType' ? props.direction : null)}
                            onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'orderType' })}>
                            Order Type</Table.HeaderCell>
                        <Table.HeaderCell {...sortDirection(props.column === 'foodTypeName' ? props.direction : null)}
                            onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'foodTypeName' })}>
                            Food Type</Table.HeaderCell>
                        <Table.HeaderCell {...sortDirection(props.column === 'flavorName' ? props.direction : null)}
                            onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'flavorName' })}>
                            Flavor</Table.HeaderCell>
                        <Table.HeaderCell {...sortDirection(props.column === 'orderItems' ? props.direction : null)}
                            onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'orderItems' })}>
                            Items</Table.HeaderCell>
                        <Table.HeaderCell {...sortDirection(props.column === 'quantity' ? props.direction : null)}
                            onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'quantity' })}>
                            Qty.</Table.HeaderCell>
                        <Table.HeaderCell {...sortDirection(props.column === 'status' ? props.direction : null)}
                            onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'status' })}>
                            Order status</Table.HeaderCell>
                        <Table.HeaderCell {...sortDirection(props.column === 'orderDate' ? props.direction : null)}
                            onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'orderDate' })}>
                            Order Date</Table.HeaderCell>
                        <Table.HeaderCell {...sortDirection(props.column === 'foodPricing' ? props.direction : null)}
                            onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'foodPricing' })}>
                            Food Price</Table.HeaderCell>
                        <Table.HeaderCell {...sortDirection(props.column === 'shippingFees' ? props.direction : null)}
                            onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'shippingFees' })}>
                            Shipping Fee</Table.HeaderCell>
                        <Table.HeaderCell {...sortDirection(props.column === 'foodPriceAndShipping' ? props.direction : null)}
                            onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'foodPriceAndShipping' })}>
                            Total</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {props.orders && props.orders.map(
                        (order: FoodOrder) => {
                            // The four `parseFloat` calls in this file are handed a
                            // `number | string` where a `string` is declared. Left
                            // standing rather than asserted: the parent normalises
                            // both fields to numbers first, so the value really is
                            // often a number and claiming otherwise would be false.
                            shippingFeesTotal += Object.prototype.hasOwnProperty.call(order, 'shippingFees')? parseFloat(String(order.shippingFees)):0
                            foodPriceTotal += Object.prototype.hasOwnProperty.call(order, 'foodPricing')? parseFloat(String(order.foodPricing)):0
                            return (
                                <Table.Row>
                                    <Table.Cell collapsing content={<Checkbox checked={props.selectedOrderIds.includes(order.orderId!)} onChange={(e, checkprops) => props.addExportData(e, checkprops, order.orderId)}/>} />
                                    <Table.Cell>{order.orderId}</Table.Cell>
                                    <Table.Cell>{order.orderName}</Table.Cell>
                                    <Table.Cell>{order.orderType}</Table.Cell>
                                    <Table.Cell>{order.foodTypeName}</Table.Cell>
                                    <Table.Cell>{order.flavorName}</Table.Cell>
                                    <Table.Cell>{order.orderItems? order.orderItems.length: 'None'}</Table.Cell>
                                    <Table.Cell>{order.quantity}</Table.Cell>
                                    <Table.Cell>{order.status ? order.status.toString().toUpperCase() : ''}</Table.Cell>
                                    <Table.Cell>{order.orderDate ? moment.utc(order.orderDate).local().format('YYYY-MM-DD hh:mm a') : ''}</Table.Cell>
                                    <Table.Cell>{Object.prototype.hasOwnProperty.call(order, 'foodPricing')? parseFloat(String(order.foodPricing)).toFixed(2):'NA'}</Table.Cell>
                                    <Table.Cell>{Object.prototype.hasOwnProperty.call(order, 'shippingFees')? parseFloat(String(order.shippingFees)).toFixed(2):'NA'}</Table.Cell>
                                    {/*
                                      * `foodPriceAndShipping` is `number | false`
                                      * (see {@link FoodOrder}); the truthiness
                                      * guard already excludes `false`, and the
                                      * assertion records that rather than re-testing.
                                      */}
                                    <Table.Cell>{order?.foodPriceAndShipping ? order?.foodPriceAndShipping?.toFixed(2):0.00}</Table.Cell>
                                </Table.Row>
                            )
                        }
                    )}
                    {!props.order && <Loader inline/>}
                </Table.Body>
                <Table.Footer fullWidth>
                    <Table.Row>
                        <Table.HeaderCell />
                        <Table.HeaderCell />
                        <Table.HeaderCell />
                        <Table.HeaderCell />
                        <Table.HeaderCell />
                        <Table.HeaderCell />
                        <Table.HeaderCell />
                        <Table.HeaderCell />
                        <Table.HeaderCell />
                        <Table.HeaderCell />
                        <Table.HeaderCell>{foodPriceTotal.toFixed(2)}</Table.HeaderCell>
                        <Table.HeaderCell>{shippingFeesTotal.toFixed(2)}</Table.HeaderCell>
                        <Table.HeaderCell>{(foodPriceTotal+shippingFeesTotal).toFixed(2)}</Table.HeaderCell>
                    </Table.Row>
                </Table.Footer>
            </Table>
        </div>
    )
}

export default InvoiceTable;